import WeatherApi from '@/api/config/weatherApi'
import { RootState } from '@/store/stores'
import { SearchParams } from '@/types'
import { WeatherReportParams } from '@/types/weather'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  initialState,
  WeatherBarometerData,
  WeatherRainData,
  WeatherReportData,
  WeatherWindDirectionData,
  WeatherReportFields,
  getColumnDetails,
} from '@store/slices/WeatherDashboard/types'


import { staticConvertToMetric } from '@/hooks/useUnits'

export const fetchLatestWeatherReport = createAsyncThunk(
  'weatherDashboard/fetchLatestWeatherReport',
  async (params: WeatherReportParams, { rejectWithValue }) => {
    try {
      const { data } = await WeatherApi.getWeatherReport(params)

      const weatherData = data.map((item: any) => ({
        timeSpan: item.timeSpan,
        rainFall: convertValueToMetric(
          item.rain_Fall,
          WeatherReportFields.RainFall.columnName,
        ),
        windSpeedAvg: convertValueToMetric(
          item.wind_Speed_Avg,
          WeatherReportFields.WindSpeedAvg.columnName,
        ),
        windSpeedHigh: convertValueToMetric(
          item.wind_Speed_High,
          WeatherReportFields.WindSpeedHigh.columnName,
        ),
        windDirectionHigh: convertValueToMetric(
          item.wind_Direction_High,
          WeatherReportFields.WindDirectionHigh.columnName,
        ),
        windRun: convertValueToMetric(
          item.wind_Run,
          WeatherReportFields.WindRun.columnName,
        ),
        thWIndex: convertValueToMetric(
          item.thW_Index,
          WeatherReportFields.THWIndex.columnName,
        ),
        temperatureOutsideAvg: convertValueToMetric(
          item.temprature_Outside_Avg,
          WeatherReportFields.TemperatureOutsideAvg.columnName,
        ),
        temperatureOutsideHigh: convertValueToMetric(
          item.temprature_Outside_High,
          WeatherReportFields.TemperatureOutsideHigh.columnName,
        ),
        temperatureOutsideLow: convertValueToMetric(
          item.temprature_Outside_Low,
          WeatherReportFields.TemperatureOutsideLow.columnName,
        ),
        temperatureWindchillAvg: convertValueToMetric(
          item.temprature_Windchill_Avg,
          WeatherReportFields.TemperatureWindchillAvg.columnName,
        ),
        temperatureHeatIndexAvg: convertValueToMetric(
          item.temprature_HeatIndex_Avg,
          WeatherReportFields.TemperatureHeatIndexAvg.columnName,
        ),
        temperatureDewPointAvg: convertValueToMetric(
          item.temprature_Dewpoint_Avg,
          WeatherReportFields.TemperatureDewPointAvg.columnName,
        ),
        temperatureWetBulbAvg: convertValueToMetric(
          item.temprature_Wetbulb_Avg,
          WeatherReportFields.TemperatureWetBulbAvg.columnName,
        ),
        rainRateHigh: convertValueToMetric(
          item.rain_Rate_High,
          WeatherReportFields.RainRateHigh.columnName,
        ),
        heatingDegreeDays: convertValueToMetric(
          item.heating_DegreeDays,
          WeatherReportFields.HeatingDegreeDays.columnName,
        ),
        coolingDegreeDays: convertValueToMetric(
          item.cooling_DegreeDays,
          WeatherReportFields.CoolingDegreeDays.columnName,
        ),
        thsWIndex: convertValueToMetric(
          item.thsW_Index,
          WeatherReportFields.THSWIndex.columnName,
        ),
        solarRadAvg: convertValueToMetric(
          item.solar_Rad_Avg,
          WeatherReportFields.SolarRadAvg.columnName,
        ),
        solarEnergy: convertValueToMetric(
          item.solar_Energy,
          WeatherReportFields.SolarEnergy.columnName,
        ),
        highSolarRad: convertValueToMetric(
          item.high_Solar_Rad,
          WeatherReportFields.HighSolarRad.columnName,
        ),
        uVIndexAvg: convertValueToMetric(
          item.uV_Index_Avg,
          WeatherReportFields.UVIndexAvg.columnName,
        ),
        uVDose: convertValueToMetric(
          item.uV_Dose,
          WeatherReportFields.UVDose.columnName,
        ),
        highUVIndex: convertValueToMetric(
          item.high_UV_Index,
          WeatherReportFields.HighUVIndex.columnName,
        ),
        et: convertValueToMetric(item.et, WeatherReportFields.ET.columnName),
        humidity: convertValueToMetric(
          item.humidity,
          WeatherReportFields.Humidity.columnName,
        ),
      })) as WeatherReportData[]

      return { weatherStationId: params.StationId, weatherData: weatherData }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetch24hData = createAsyncThunk(
  'weatherDashboard/fetch24hData',
  async (params: WeatherReportParams, { rejectWithValue }) => {
    try {
      const { data } = await WeatherApi.getWeatherReport(params)

      const weatherData = data.map((item: any) => ({
        timeSpan: item.timeSpan,
        rainFall: convertValueToMetric(
          item.rain_Fall,
          WeatherReportFields.RainFall.columnName,
        ),
        windSpeedAvg: convertValueToMetric(
          item.wind_Speed_Avg,
          WeatherReportFields.WindSpeedAvg.columnName,
        ),
        windSpeedHigh: convertValueToMetric(
          item.wind_Speed_High,
          WeatherReportFields.WindSpeedHigh.columnName,
        ),
        windDirectionHigh: convertValueToMetric(
          item.wind_Direction_High,
          WeatherReportFields.WindDirectionHigh.columnName,
        ),
        windRun: convertValueToMetric(
          item.wind_Run,
          WeatherReportFields.WindRun.columnName,
        ),
        thWIndex: convertValueToMetric(
          item.thW_Index,
          WeatherReportFields.THWIndex.columnName,
        ),
        temperatureOutsideAvg: convertValueToMetric(
          item.temprature_Outside_Avg,
          WeatherReportFields.TemperatureOutsideAvg.columnName,
        ),
        temperatureOutsideHigh: convertValueToMetric(
          item.temprature_Outside_High,
          WeatherReportFields.TemperatureOutsideHigh.columnName,
        ),
        temperatureOutsideLow: convertValueToMetric(
          item.temprature_Outside_Low,
          WeatherReportFields.TemperatureOutsideLow.columnName,
        ),
        temperatureWindchillAvg: convertValueToMetric(
          item.temprature_Windchill_Avg,
          WeatherReportFields.TemperatureWindchillAvg.columnName,
        ),
        temperatureHeatIndexAvg: convertValueToMetric(
          item.temprature_HeatIndex_Avg,
          WeatherReportFields.TemperatureHeatIndexAvg.columnName,
        ),
        temperatureDewPointAvg: convertValueToMetric(
          item.temprature_Dewpoint_Avg,
          WeatherReportFields.TemperatureDewPointAvg.columnName,
        ),
        temperatureWetBulbAvg: convertValueToMetric(
          item.temprature_Wetbulb_Avg,
          WeatherReportFields.TemperatureWetBulbAvg.columnName,
        ),
        rainRateHigh: convertValueToMetric(
          item.rain_Rate_High,
          WeatherReportFields.RainRateHigh.columnName,
        ),
        heatingDegreeDays: convertValueToMetric(
          item.heating_DegreeDays,
          WeatherReportFields.HeatingDegreeDays.columnName,
        ),
        coolingDegreeDays: convertValueToMetric(
          item.cooling_DegreeDays,
          WeatherReportFields.CoolingDegreeDays.columnName,
        ),
        thsWIndex: convertValueToMetric(
          item.thsW_Index,
          WeatherReportFields.THSWIndex.columnName,
        ),
        solarRadAvg: convertValueToMetric(
          item.solar_Rad_Avg,
          WeatherReportFields.SolarRadAvg.columnName,
        ),
        solarEnergy: convertValueToMetric(
          item.solar_Energy,
          WeatherReportFields.SolarEnergy.columnName,
        ),
        highSolarRad: convertValueToMetric(
          item.high_Solar_Rad,
          WeatherReportFields.HighSolarRad.columnName,
        ),
        uVIndexAvg: convertValueToMetric(
          item.uV_Index_Avg,
          WeatherReportFields.UVIndexAvg.columnName,
        ),
        uVDose: convertValueToMetric(
          item.uV_Dose,
          WeatherReportFields.UVDose.columnName,
        ),
        highUVIndex: convertValueToMetric(
          item.high_UV_Index,
          WeatherReportFields.HighUVIndex.columnName,
        ),
        et: convertValueToMetric(item.et, WeatherReportFields.ET.columnName),
        humidity: convertValueToMetric(
          item.humidity,
          WeatherReportFields.Humidity.columnName,
        ),
      })) as WeatherReportData[]

      return { weatherStationId: params.StationId, weatherData: weatherData }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

function convertValueToMetric(value: number, columnName: string): number {
  const needConversion = [
    WeatherReportFields.TemperatureOutsideAvg.columnName.toLowerCase(),
    WeatherReportFields.ET.columnName.toLowerCase(),
    // WeatherReportFields.RainFall.columnName.toLowerCase(),
    WeatherReportFields.WindSpeedAvg.columnName.toLowerCase(),
    WeatherReportFields.THWIndex.columnName.toLowerCase(),
    WeatherReportFields.THSWIndex.columnName.toLowerCase(),
    WeatherReportFields.TemperatureWindchillAvg.columnName.toLowerCase(),
    WeatherReportFields.TemperatureHeatIndexAvg.columnName.toLowerCase(),
    WeatherReportFields.TemperatureDewPointAvg.columnName.toLowerCase(),
    WeatherReportFields.TemperatureWetBulbAvg.columnName.toLowerCase(),
  ]

  const columnDetail = getColumnDetails(columnName)
  if (!needConversion.includes(columnName.toLowerCase()) || !columnDetail) {
    return value
  }

  return staticConvertToMetric(
    value,
    columnDetail.metricUnit,
    columnDetail.imperialUnit,
  )
}

export const fetchRainData = createAsyncThunk(
  'weatherDashboard/fetchRainData',
  async (
    params: {
      interfaceId: number
      stationId: number
      searchParams: SearchParams
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await WeatherApi.getWeatherRain(
        params.interfaceId,
        params.stationId,
        {
          ...params.searchParams,
        },
      )

      return {
        weatherStationId: params.stationId,
        rainData: data as WeatherRainData[],
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchWindDirectionData = createAsyncThunk(
  'weatherDashboard/fetchWindDirectionData',
  async (
    params: {
      interfaceId: number
      stationId: number
      searchParams: SearchParams
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await WeatherApi.getWeatherWindDirection(
        params.interfaceId,
        params.stationId,
        {
          ...params.searchParams,
        },
      )

      return {
        weatherStationId: params.stationId,
        windDirectionData: data as WeatherWindDirectionData,
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchBarometerData = createAsyncThunk(
  'weatherDashboard/fetchBarometerData',
  async (
    params: {
      interfaceId: number
      stationId: number
      from: string
      to: string
      searchParams: SearchParams
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await WeatherApi.getWeatherBarometerHistory(
        params.interfaceId,
        params.stationId,
        params.from,
        params.to,
        {
          ...params.searchParams,
        },
      )

      return {
        weatherStationId: params.stationId,
        barometerData: data as WeatherBarometerData[],
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const weatherDashboardSlice = createSlice({
  name: 'diagnosticMapView',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchLatestWeatherReport
      .addCase(fetchLatestWeatherReport.pending, (state) => {
        state.isLoadingLatestWeatherReport = true
      })
      .addCase(fetchLatestWeatherReport.fulfilled, (state, action) => {
        state.isLoadingLatestWeatherReport = false
        if (action.payload.weatherData && action.payload.weatherStationId) {
          state.latestWeatherReport[`${action.payload.weatherStationId}`] =
            action.payload.weatherData[0]
        }
      })
      .addCase(fetchLatestWeatherReport.rejected, (state) => {
        state.isLoadingLatestWeatherReport = false
      })

      // fetch24hData
      .addCase(fetch24hData.pending, (state) => {
        state.isLoading24hTemperature = true
      })
      .addCase(fetch24hData.fulfilled, (state, action) => {
        state.isLoading24hTemperature = false
        if (action.payload.weatherData && action.payload.weatherStationId) {
          state.data24h[`${action.payload.weatherStationId}`] =
            action.payload.weatherData
        }
      })
      .addCase(fetch24hData.rejected, (state) => {
        state.isLoading24hTemperature = false
      })

      // fetchRainData
      .addCase(fetchRainData.pending, (state) => {
        state.isLoadingRain = true
      })
      .addCase(fetchRainData.fulfilled, (state, action) => {
        state.isLoadingRain = false
        if (action.payload.rainData && action.payload.weatherStationId) {
          state.rainData[`${action.payload.weatherStationId}`] =
            action.payload.rainData
        }
      })
      .addCase(fetchRainData.rejected, (state) => {
        state.isLoadingRain = false
      })

      // fetchWindDirectionData
      .addCase(fetchWindDirectionData.pending, (state) => {
        state.isLoadingWindDirection = true
      })
      .addCase(fetchWindDirectionData.fulfilled, (state, action) => {
        state.isLoadingWindDirection = false
        if (
          action.payload.windDirectionData &&
          action.payload.weatherStationId
        ) {
          state.windDirectionData[`${action.payload.weatherStationId}`] =
            action.payload.windDirectionData
        }
      })
      .addCase(fetchWindDirectionData.rejected, (state) => {
        state.isLoadingWindDirection = false
      })

      // fetchBarometerData
      .addCase(fetchBarometerData.pending, (state) => {
        state.isLoadingBarometer = true
      })
      .addCase(fetchBarometerData.fulfilled, (state, action) => {
        state.isLoadingBarometer = false
        if (action.payload.barometerData && action.payload.weatherStationId) {
          state.barometerData[`${action.payload.weatherStationId}`] =
            action.payload.barometerData
        }
      })
      .addCase(fetchBarometerData.rejected, (state) => {
        state.isLoadingBarometer = false
      })
  },
})

export const weatherDashboardState = (state: RootState) =>
  state.weatherDashboard

export default weatherDashboardSlice.reducer
